import * as React from 'react'

import { Box, Button, Form, Title } from '@factorialco/deprecated-design-system'
import { ResourceFieldDataFragment, ValueDataFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import PanesPadForm from 'modules/core/components/PanesPadForm'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { employeeLegalEntity } from 'modules/core/lib/employeeLegalEntity'
import i18n from 'modules/core/lib/i18n'

import CustomFieldsForm from 'modules/custom_resources/components/CustomFields/CustomFieldsForm'
import type Employee from 'modules/employees/store/models/Employee'
import { CompanyInstallations } from 'modules/marketplace/store/collections/companyInstallations'
import Codes from 'modules/payroll_integrations/components/Employee/Codes'
import integrationsDescriptor from 'modules/payroll_integrations/components/IntegrationsDescriptor'

import type { IrpfHandle } from './_Irpf'
import Irpf from './_Irpf'

interface Props {
  employee: Employee
  readonly: boolean
  customResourceFieldsData: ResourceFieldDataFragment[]
  customValuesData: ValueDataFragment[]
  employeeCanEditPersonalInfo: boolean
}

const PayrollBlock = ({
  employee,
  readonly,
  customResourceFieldsData,
  customValuesData,
  employeeCanEditPersonalInfo,
}: Props) => {
  const codeRef = React.useRef<Array<Record<string, any>>>([])
  const irpfRef = React.useRef<IrpfHandle>(null)
  const [dirty, setDirty] = React.useState(false)
  const companyInstallations = useLazyRef(() => new CompanyInstallations()).current
  const { loading: loadingIntegrations } = useAsyncData(() => companyInstallations.fetch(), [])

  useUnsafeEffect(() => {
    codeRef.current = codeRef.current.slice(
      0,
      integrationsDescriptor.enabled(companyInstallations).length
    )
  }, [companyInstallations, loadingIntegrations])

  const onChange = () => {
    const anyDirty = codeRef.current.find((form) => form.isDirty())

    if (!anyDirty && irpfRef.current && !irpfRef.current.isDirty()) setDirty(false)
    else setDirty(true)
  }

  const onSubmit = () => {
    codeRef.current.forEach((form) => {
      form.onSubmit()
    })
    if (irpfRef.current) {
      irpfRef.current.onSubmit()
    }
    track(trackingEvents.employee.editEmployeeInformationSubmit, {
      fields_edited: ['Irpf'],
      legal_entity_country: employeeLegalEntity(employee).country,
      layout_version: 'v1',
    })
  }

  const integrations = integrationsDescriptor.enabled(companyInstallations)
  const hasIntegrations = integrations.length > 0

  if (customResourceFieldsData.length === 0 && !employeeCanEditPersonalInfo && !hasIntegrations) {
    return null
  }

  return (
    <PanesPadForm direction="horizontal" boxed>
      <Title
        variant="500"
        illustration="money-bag"
        title={i18n.t('employee_form.custom_fields.payroll_information.title')}
        description={i18n.t('employee_form.custom_fields.payroll_information.description')}
      />
      <>
        {hasIntegrations && (
          <Codes
            employee={employee}
            readonly={readonly}
            codeRef={codeRef}
            onChange={onChange}
            installedIntegrations={companyInstallations}
          />
        )}
        <CustomFieldsForm
          boxed={false}
          customResourceFieldsData={customResourceFieldsData}
          customValuesData={customValuesData}
          padFormDirection="vertical"
          readonly={readonly}
          valuableId={employee.id}
          valuableType="Employee"
          customFieldsOnly
          currency={employee?.attributes.legal_entity_currency}
        />
        {employeeCanEditPersonalInfo && (
          <Irpf employee={employee} readonly={readonly} ref={irpfRef} onChange={onChange} />
        )}
        {dirty && (
          <Form.Buttons>
            <Box paddingTop="s24">
              <Button onClick={onSubmit}>{i18n.t('common.submit')}</Button>
            </Box>
          </Form.Buttons>
        )}
      </>
    </PanesPadForm>
  )
}

export default observer(PayrollBlock)
